import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import {
  LuArchive,
  LuCheck,
  LuCheckCheck,
  LuInbox,
  LuMail,
  LuMailOpen,
  LuSearch,
  LuSettings,
  LuTrash2,
  LuX,
} from "react-icons/lu";

import {
  useGetNotificationCountsQuery,
  useGetNotificationListQuery,
} from "../../slices/NotificationCenterSlice";
import { GetPageTitle } from "../../support/ScrollToTop";
import { modalCancel, PortalModal } from "../clientPortal/PortalModal";
import { CountPill, KindGlyph } from "./NotificationParts";
import { NotificationRow } from "./NotificationRow";
import {
  NotificationCategory,
  NotificationView as View,
} from "./notifications.types";
import {
  categoryLabel,
  groupNotifications,
  NOTIFICATION_CATEGORIES,
} from "./notifications.utils";
import { MarkAllDoneConfirm } from "./MarkAllDoneConfirm";
import { NotificationSettingsModal } from "./NotificationSettingsModal";
import { useNotificationActions } from "./useNotificationActions";

const PAGE = 50;
const deleteButton =
  "h-10 rounded-[9px] bg-[#D92D20] px-5 text-[13.5px] font-extrabold text-white hover:bg-[#B42318]";

const RailItem: FC<{
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  count?: number;
}> = ({ active, onClick, icon, label, count }) => (
  <button
    type={"button"}
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    className={`flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-[7px] text-left text-[13.5px] font-bold ${
      active
        ? "bg-[#6B4EFF] text-white"
        : "text-[#4A5163] hover:bg-[#F3F1FF] hover:text-[#1F2433]"
    }`}>
    <span className={"flex w-5 justify-center text-[16px]"}>{icon}</span>
    <span className={"min-w-0 flex-1 truncate"}>{label}</span>
    <CountPill count={count} active={active} />
  </button>
);

const BulkButton: FC<{
  onClick: () => void;
  icon: ReactNode;
  label: string;
  danger?: boolean;
}> = ({ onClick, icon, label, danger }) => (
  <button
    type={"button"}
    onClick={onClick}
    className={`flex h-8 items-center gap-1.5 rounded-[8px] px-2.5 text-[13px] font-bold ${
      danger
        ? "text-[#B42318] hover:bg-[#FDECEC]"
        : "text-[#3A4256] hover:bg-[#F0EDFF] hover:text-[#6B4EFF]"
    }`}>
    {icon}
    {label}
  </button>
);

/** The Notifications page (`/notifications`). */
export const NotificationView: FC = () => {
  const { orgId, open, setRead, setDone, doneAll, deleteForever } =
    useNotificationActions();
  const [confirmAllDone, setConfirmAllDone] = useState(false);
  const [view, setView] = useState<View>("inbox");
  const [category, setCategory] = useState<NotificationCategory | "all">("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [pages, setPages] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    document.title = GetPageTitle("Notifications");
  }, []);

  // Wait until typing pauses before searching.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // A new list starts from the first page with nothing selected.
  useEffect(() => {
    setPages(1);
    setSelected([]);
  }, [view, category, search, unreadOnly]);

  const { data: counts } = useGetNotificationCountsQuery(
    { orgId },
    { skip: !orgId },
  );
  const doneAvailable = counts?.doneAvailable !== false;
  const effectiveView: View = doneAvailable ? view : "inbox";

  const take = PAGE * pages;
  const {
    data: items = [],
    isLoading,
    isFetching,
    isError,
  } = useGetNotificationListQuery(
    {
      orgId,
      view: effectiveView,
      category,
      search: search || undefined,
      unreadOnly: unreadOnly || undefined,
      take,
    },
    { skip: !orgId },
  );

  const groups = useMemo(() => groupNotifications(items), [items]);
  // Forget selections that left the list (done, deleted, filtered out).
  useEffect(() => {
    setSelected((current) =>
      current.filter((id) => items.some((item) => item.id === id)),
    );
  }, [items]);

  const allSelected = items.length > 0 && selected.length === items.length;
  const selectedItems = items.filter((item) => selected.includes(item.id));
  const anyUnreadSelected = selectedItems.some((item) => !item.isRead);

  const toggle = (id: string, on: boolean) =>
    setSelected((current) =>
      on ? [...current, id] : current.filter((value) => value !== id),
    );

  // How many "Mark all as done" moves: the inbox total, or the loaded list
  // when it is complete and unfiltered; otherwise not known exactly.
  const allDoneCount =
    category === "all"
      ? counts?.inbox ?? null
      : !search && !unreadOnly && items.length < take
      ? items.length
      : null;

  const heading =
    effectiveView === "done"
      ? "Done"
      : category === "all"
      ? "Inbox"
      : categoryLabel(category);

  const emptyText =
    search || unreadOnly
      ? {
          title: "No notifications match",
          text: "Try a different search, or show all notifications.",
        }
      : effectiveView === "done"
      ? {
          title: "Nothing here yet",
          text: "Notifications you mark as done move here, so your inbox only holds what still needs you.",
        }
      : {
          title: "You're all caught up",
          text: "New notifications will show up here.",
        };

  return (
    <div
      className={
        "h-full min-w-0 flex-1 overflow-y-auto bg-[#F7F8FA] font-Manrope"
      }>
      <div className={"mx-auto w-full max-w-[1560px] pb-6"}>
        <div
          className={
            "mb-[22px] flex flex-wrap items-start justify-between gap-4"
          }>
          <div>
            <h1
              className={
                "mb-1 text-[24px] font-extrabold tracking-[-0.5px] text-[#1F2433]"
              }>
              Notifications
            </h1>
            <p className={"m-0 text-[14.5px] text-[#6B7185]"}>
              What needs your attention, newest first. Mark things as done to
              keep your inbox short.
            </p>
          </div>
          <div className={"flex items-center gap-2"}>
            {doneAvailable && effectiveView === "inbox" && items.length > 0 && (
              <button
                type={"button"}
                onClick={() => setConfirmAllDone(true)}
                className={
                  "flex h-[38px] items-center gap-2 rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-3.5 text-[13.5px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
                }>
                <LuCheckCheck />
                {category === "all"
                  ? "Mark all as done"
                  : `Mark all ${categoryLabel(category).toLowerCase()} as done`}
              </button>
            )}
            <button
              type={"button"}
              onClick={() => setSettingsOpen(true)}
              className={
                "flex h-[38px] items-center gap-2 rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-3.5 text-[13.5px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
              }>
              <LuSettings />
              Settings
            </button>
          </div>
        </div>

        <div
          className={
            "grid grid-cols-1 items-start gap-5 md:grid-cols-[232px_1fr]"
          }>
          <nav
            aria-label={"Notification folders"}
            className={
              "flex flex-col gap-0.5 rounded-[14px] border border-[#E8E8EC] bg-white p-2.5"
            }>
            <RailItem
              active={effectiveView === "inbox" && category === "all"}
              onClick={() => {
                setView("inbox");
                setCategory("all");
              }}
              icon={<LuInbox />}
              label={"Inbox"}
              count={counts?.unread}
            />
            {doneAvailable && (
              <RailItem
                active={effectiveView === "done"}
                onClick={() => {
                  setView("done");
                  setCategory("all");
                }}
                icon={<LuArchive />}
                label={"Done"}
              />
            )}
            <div
              className={
                "mb-1 mt-3 px-2.5 text-[11px] font-extrabold uppercase tracking-[0.6px] text-[#9097A8]"
              }>
              Categories
            </div>
            {NOTIFICATION_CATEGORIES.map((c) => (
              <RailItem
                key={c.key}
                active={effectiveView === "inbox" && category === c.key}
                onClick={() => {
                  setView("inbox");
                  setCategory(c.key);
                }}
                icon={
                  <KindGlyph
                    category={c.key}
                    colored={!(effectiveView === "inbox" && category === c.key)}
                  />
                }
                label={c.label}
                count={counts?.categories?.[c.key]}
              />
            ))}
          </nav>

          <section className={"min-w-0"}>
            <div
              className={"mb-4 flex min-h-[42px] flex-wrap items-center gap-3"}>
              {selected.length > 0 ? (
                <div
                  className={
                    "flex flex-1 flex-wrap items-center gap-1 rounded-[10px] border border-[#D9D1FF] bg-[#F7F5FF] px-2 py-1"
                  }>
                  <span
                    className={
                      "px-2 text-[13px] font-extrabold text-[#4B33D6]"
                    }>
                    {selected.length} selected
                  </span>
                  {anyUnreadSelected ? (
                    <BulkButton
                      icon={<LuMailOpen />}
                      label={"Mark as read"}
                      onClick={() => setRead(selected, true)}
                    />
                  ) : (
                    <BulkButton
                      icon={<LuMail />}
                      label={"Mark as unread"}
                      onClick={() => setRead(selected, false)}
                    />
                  )}
                  {doneAvailable && (
                    <BulkButton
                      icon={
                        effectiveView === "done" ? <LuInbox /> : <LuCheck />
                      }
                      label={
                        effectiveView === "done" ? "Move to inbox" : "Done"
                      }
                      onClick={() =>
                        setDone(selectedItems, effectiveView !== "done")
                      }
                    />
                  )}
                  <BulkButton
                    icon={<LuTrash2 />}
                    label={"Delete"}
                    danger
                    onClick={() => setConfirmDelete(true)}
                  />
                  <button
                    type={"button"}
                    aria-label={"Clear selection"}
                    onClick={() => setSelected([])}
                    className={
                      "ml-auto flex h-8 w-8 items-center justify-center rounded-[8px] text-[#6B7185] hover:bg-white"
                    }>
                    <LuX />
                  </button>
                </div>
              ) : (
                <>
                  <span
                    className={
                      "flex min-w-[220px] flex-1 items-center gap-[9px] rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-[13px] py-[8px] focus-within:border-[#6B4EFF]"
                    }>
                    <LuSearch className={"text-[#9097A8]"} />
                    <input
                      id={"notification-search"}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder={"Search by client, work item or person"}
                      className={
                        "flex-1 border-none bg-transparent text-[14px] text-[#1F2433] outline-none"
                      }
                    />
                    {searchInput && (
                      <button
                        type={"button"}
                        aria-label={"Clear search"}
                        onClick={() => setSearchInput("")}
                        className={"text-[#9097A8] hover:text-[#1F2433]"}>
                        <LuX />
                      </button>
                    )}
                  </span>
                  <div
                    role={"group"}
                    aria-label={"Show"}
                    className={
                      "flex rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white p-[3px]"
                    }>
                    {[
                      { key: false, label: "All" },
                      { key: true, label: "Unread" },
                    ].map((option) => (
                      <button
                        key={option.label}
                        type={"button"}
                        aria-pressed={unreadOnly === option.key}
                        onClick={() => setUnreadOnly(option.key)}
                        className={`rounded-[7px] px-3 py-[5px] text-[13px] font-bold ${
                          unreadOnly === option.key
                            ? "bg-[#6B4EFF] text-white"
                            : "text-[#5B6678] hover:text-[#1F2433]"
                        }`}>
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className={"mb-2 flex items-center justify-between px-1"}>
              <label
                className={
                  "flex cursor-pointer items-center gap-2 text-[12.5px] font-bold text-[#6B7185]"
                }>
                <input
                  type={"checkbox"}
                  aria-label={"Select all"}
                  checked={allSelected}
                  disabled={!items.length}
                  onChange={(e) =>
                    setSelected(e.target.checked ? items.map((i) => i.id) : [])
                  }
                  className={"h-4 w-4 cursor-pointer accent-[#6B4EFF]"}
                />
                {heading}
              </label>
              {isFetching && !isLoading && (
                <span className={"text-[12px] text-[#9097A8]"}>Updating…</span>
              )}
            </div>

            {isLoading ? (
              <div
                className={
                  "rounded-[13px] border border-[#E8E8EC] bg-white p-[54px] text-center text-[14px] text-[#9097A8]"
                }>
                Loading…
              </div>
            ) : isError ? (
              <div
                className={
                  "rounded-[13px] border border-[#E8E8EC] bg-white p-[54px] text-center text-[14px] text-[#9097A8]"
                }>
                Your notifications couldn&apos;t be loaded. Please try again.
              </div>
            ) : items.length === 0 ? (
              <div
                className={
                  "flex flex-col items-center gap-1.5 rounded-[13px] border border-[#E8E8EC] bg-white px-6 py-[54px] text-center"
                }>
                <span
                  className={
                    "mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-[#E9F7EF] text-[22px] text-[#2E9E62]"
                  }>
                  <LuCheck />
                </span>
                <span className={"text-[15px] font-extrabold text-[#1F2433]"}>
                  {emptyText.title}
                </span>
                <span className={"max-w-[380px] text-[13px] text-[#6B7185]"}>
                  {emptyText.text}
                </span>
              </div>
            ) : (
              <>
                {groups.map((group) => (
                  <div key={group.label} className={"mb-5"}>
                    <div
                      className={
                        "mb-2 px-1 text-[12px] font-extrabold uppercase tracking-[0.5px] text-[#9097A8]"
                      }>
                      {group.label}
                    </div>
                    <div
                      className={
                        "overflow-hidden rounded-[13px] border border-[#E8E8EC] bg-white"
                      }>
                      {group.items.map((item) => (
                        <NotificationRow
                          key={item.id}
                          item={item}
                          selected={selected.includes(item.id)}
                          onSelect={(on) => toggle(item.id, on)}
                          onOpen={() => open(item)}
                          onToggleRead={() => setRead([item.id], !item.isRead)}
                          onToggleDone={
                            doneAvailable
                              ? () => setDone([item], !item.isDone)
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
                {items.length >= take && (
                  <div className={"flex justify-center"}>
                    <button
                      type={"button"}
                      disabled={isFetching}
                      onClick={() => setPages((p) => p + 1)}
                      className={
                        "h-[36px] rounded-[8px] border border-[#DDE0E7] bg-white px-4 text-[13px] font-bold text-[#4A5163] hover:border-[#6B4EFF] hover:text-[#6B4EFF] disabled:opacity-60"
                      }>
                      {isFetching ? "Loading…" : "Show older notifications"}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {settingsOpen && (
        <NotificationSettingsModal onClose={() => setSettingsOpen(false)} />
      )}
      {confirmAllDone && (
        <MarkAllDoneConfirm
          count={allDoneCount}
          label={category === "all" ? undefined : categoryLabel(category)}
          unread={category === "all" ? counts?.unread ?? null : null}
          onCancel={() => setConfirmAllDone(false)}
          onConfirm={() => {
            setConfirmAllDone(false);
            void doneAll(category);
          }}
        />
      )}
      {confirmDelete && (
        <PortalModal
          title={
            selected.length === 1
              ? "Delete this notification?"
              : `Delete ${selected.length} notifications?`
          }
          onClose={() => setConfirmDelete(false)}
          width={440}
          footer={
            <>
              <button
                type={"button"}
                className={modalCancel}
                onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button
                type={"button"}
                className={deleteButton}
                onClick={() => {
                  setConfirmDelete(false);
                  void deleteForever(selected);
                }}>
                Delete
              </button>
            </>
          }>
          <p className={"m-0 text-[14px] text-[#4A5163]"}>
            {doneAvailable
              ? "Deleted notifications can't be brought back. To just clear them from your inbox, use Done instead."
              : "Deleted notifications can't be brought back."}
          </p>
        </PortalModal>
      )}
    </div>
  );
};
